import { db, auth } from './config';
import { collection, addDoc, updateDoc, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { isValidSouthAfricanPhone } from '../utils/validation';

const getCurrentStaffEmail = () => auth.currentUser?.email || 'unknown';

const normalizePhone = (phone) => phone?.replace(/\D/g, '') || '';

export const isPhoneNumberTaken = async (phoneNumber, excludeClientId = null) => {
  const normalized = normalizePhone(phoneNumber);
  if (!normalized) return false;
  const q = query(collection(db, 'clients'), where('phoneNumber', '==', normalized));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return false;
  if (excludeClientId) {
    for (const doc of snapshot.docs) {
      if (doc.id !== excludeClientId) return true;
    }
    return false;
  }
  return true;
};

export const createClient = async (clientData) => {
  const normalizedPhone = normalizePhone(clientData.phoneNumber);
  if (!normalizedPhone) throw new Error('Phone number is required.');
  if (!isValidSouthAfricanPhone(normalizedPhone)) {
    throw new Error('Invalid South African phone number. Use format 0721234567');
  }
  const phoneExists = await isPhoneNumberTaken(normalizedPhone);
  if (phoneExists) throw new Error(`Client with phone number ${normalizedPhone} already exists.`);
  
  const staffEmail = getCurrentStaffEmail();
  const now = new Date().toISOString();
  const client = {
    ...clientData,
    phoneNumber: normalizedPhone,
    name: clientData.name,
    createdBy: staffEmail,
    createdAt: now,
    updatedBy: staffEmail,
    updatedAt: now
  };
  const docRef = await addDoc(collection(db, 'clients'), client);
  return { clientId: docRef.id, ...client };
};

export const updateClient = async (clientId, clientData) => {
  const staffEmail = getCurrentStaffEmail();
  const clientRef = doc(db, 'clients', clientId);
  const updatePayload = { ...clientData };
  if (clientData.phoneNumber) {
    const normalizedPhone = normalizePhone(clientData.phoneNumber);
    if (!isValidSouthAfricanPhone(normalizedPhone)) throw new Error('Invalid phone number.');
    const phoneExists = await isPhoneNumberTaken(normalizedPhone, clientId);
    if (phoneExists) throw new Error(`Phone number ${normalizedPhone} already used.`);
    updatePayload.phoneNumber = normalizedPhone;
  }
  await updateDoc(clientRef, {
    ...updatePayload,
    updatedBy: staffEmail,
    updatedAt: new Date().toISOString()
  });
};

export const searchClientByPhone = async (phoneNumber) => {
  if (!phoneNumber) return null;
  const normalized = normalizePhone(phoneNumber);
  if (!normalized) return null;
  const q = query(collection(db, 'clients'), where('phoneNumber', '==', normalized));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const docSnap = snapshot.docs[0];
  return { clientId: docSnap.id, ...docSnap.data() };
};

export const getClientById = async (clientId) => {
  const docRef = doc(db, 'clients', clientId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return { clientId: docSnap.id, ...docSnap.data() };
};

export const searchClients = async (searchTerm) => {
  if (!searchTerm?.trim()) return [];
  const searchLower = searchTerm.toLowerCase().trim();
  const allClients = await getDocs(collection(db, 'clients'));
  const matches = [];
  allClients.forEach(doc => {
    const client = doc.data();
    if (client.name && client.name.toLowerCase().includes(searchLower)) {
      matches.push({ clientId: doc.id, ...client });
    }
  });
  return matches;
};
